import axiosClientCached from '@lib/axiosClientCached';
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { AppState } from '@store/appStore';

// ── Types matching backend CourierPayoutController response ─────────────────

export interface CourierPayoutRow {
  id: number;
  uuid: string;
  courier_company_id: number;
  amount_minor: number;
  currency_id: number | null;
  courier_payout_status_type_id: number;
  parcel_id: number | null;
  transaction_reference: string | null;
  paid_at: string | null;
  created_at: string;
  updated_at: string;
  courierPayoutStatusType?: { id: number; slug: string; name: string };
  currency?: { id: number; code: string; symbol?: string };
  parcel?: { id: number; uuid: string };
}

export interface CourierPayoutStatsByStatus {
  count: number;
  total_minor: number;
}

export interface CourierPayoutStats {
  company_slug: string;
  by_status: {
    pending_delivery: CourierPayoutStatsByStatus;
    pending_payment: CourierPayoutStatsByStatus;
    paid: CourierPayoutStatsByStatus;
    failed: CourierPayoutStatsByStatus;
  };
  lifetime_paid_minor: number;
  total_pending_minor: number;
}

interface CourierPayoutState {
  pending: CourierPayoutRow[];
  history: CourierPayoutRow[];
  stats: CourierPayoutStats | null;
  loading: {
    pending: boolean;
    history: boolean;
    stats: boolean;
  };
  error: {
    pending: string | null;
    history: string | null;
    stats: string | null;
  };
}

const initialState: CourierPayoutState = {
  pending: [],
  history: [],
  stats: null,
  loading: { pending: false, history: false, stats: false },
  error: { pending: null, history: null, stats: null },
};

// ── Thunks ──────────────────────────────────────────────────────────────────

interface PaginatedResponse<T> {
  data: T[];
  meta?: { total: number; per_page: number; current_page: number; last_page: number };
}

export const fetchPendingPayouts = createAsyncThunk<
  CourierPayoutRow[],
  { companySlug?: string; perPage?: number } | undefined,
  { rejectValue: string }
>('courierPayout/fetchPending', async (args, { rejectWithValue }) => {
  try {
    const params: Record<string, string | number> = { status: 'pending_payment' };
    if (args?.companySlug) params.company_slug = args.companySlug;
    if (args?.perPage) params.per_page = args.perPage;
    const res = await axiosClientCached.get<PaginatedResponse<CourierPayoutRow>>('/billing/courier-payouts', {
      params,
      cache: false,
    });
    return res.data.data ?? [];
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } }; message?: string };
    return rejectWithValue(err.response?.data?.message ?? err.message ?? 'Failed to fetch pending payouts');
  }
});

export const fetchPayoutHistory = createAsyncThunk<
  CourierPayoutRow[],
  { companySlug?: string; perPage?: number } | undefined,
  { rejectValue: string }
>('courierPayout/fetchHistory', async (args, { rejectWithValue }) => {
  try {
    const params: Record<string, string | number> = { status: 'paid' };
    if (args?.companySlug) params.company_slug = args.companySlug;
    if (args?.perPage) params.per_page = args.perPage;
    const res = await axiosClientCached.get<PaginatedResponse<CourierPayoutRow>>('/billing/courier-payouts', {
      params,
      cache: false,
    });
    return res.data.data ?? [];
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } }; message?: string };
    return rejectWithValue(err.response?.data?.message ?? err.message ?? 'Failed to fetch payout history');
  }
});

export const fetchPayoutStats = createAsyncThunk<
  CourierPayoutStats,
  { companySlug: string },
  { rejectValue: string }
>('courierPayout/fetchStats', async ({ companySlug }, { rejectWithValue }) => {
  try {
    const res = await axiosClientCached.get<CourierPayoutStats>('/billing/courier-payouts/stats', {
      params: { company_slug: companySlug },
      cache: false,
    });
    return res.data;
  } catch (e: unknown) {
    const err = e as { response?: { data?: { message?: string } }; message?: string };
    return rejectWithValue(err.response?.data?.message ?? err.message ?? 'Failed to fetch payout stats');
  }
});

// ── Slice ───────────────────────────────────────────────────────────────────

const courierPayoutSlice = createSlice({
  name: 'courierPayout',
  initialState,
  reducers: {
    clearCourierPayoutState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPendingPayouts.pending, (s) => {
        s.loading.pending = true;
        s.error.pending = null;
      })
      .addCase(fetchPendingPayouts.fulfilled, (s, a: PayloadAction<CourierPayoutRow[]>) => {
        s.loading.pending = false;
        s.pending = a.payload;
      })
      .addCase(fetchPendingPayouts.rejected, (s, a) => {
        s.loading.pending = false;
        s.error.pending = a.payload ?? 'Unknown error';
      })
      .addCase(fetchPayoutHistory.pending, (s) => {
        s.loading.history = true;
        s.error.history = null;
      })
      .addCase(fetchPayoutHistory.fulfilled, (s, a: PayloadAction<CourierPayoutRow[]>) => {
        s.loading.history = false;
        s.history = a.payload;
      })
      .addCase(fetchPayoutHistory.rejected, (s, a) => {
        s.loading.history = false;
        s.error.history = a.payload ?? 'Unknown error';
      })
      .addCase(fetchPayoutStats.pending, (s) => {
        s.loading.stats = true;
        s.error.stats = null;
      })
      .addCase(fetchPayoutStats.fulfilled, (s, a: PayloadAction<CourierPayoutStats>) => {
        s.loading.stats = false;
        s.stats = a.payload;
      })
      .addCase(fetchPayoutStats.rejected, (s, a) => {
        s.loading.stats = false;
        s.error.stats = a.payload ?? 'Unknown error';
      });
  },
});

export const { clearCourierPayoutState } = courierPayoutSlice.actions;

// ── Selectors ───────────────────────────────────────────────────────────────

export const selectPendingPayouts = (s: AppState) => s.courierPayout.pending;
export const selectPayoutHistory = (s: AppState) => s.courierPayout.history;
export const selectPayoutStats = (s: AppState) => s.courierPayout.stats;
export const selectPendingPayoutsLoading = (s: AppState) => s.courierPayout.loading.pending;
export const selectPayoutHistoryLoading = (s: AppState) => s.courierPayout.loading.history;
export const selectPayoutStatsLoading = (s: AppState) => s.courierPayout.loading.stats;
export const selectPendingPayoutsError = (s: AppState) => s.courierPayout.error.pending;
export const selectPayoutHistoryError = (s: AppState) => s.courierPayout.error.history;
export const selectPayoutStatsError = (s: AppState) => s.courierPayout.error.stats;

export default courierPayoutSlice.reducer;
